import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError, type CompanySummary } from '../lib/api'
import { useAuth } from '../lib/auth'
import { AccountCard } from '../components/AccountCard'

type CompanyRow = CompanySummary & { created_at: string; user_count: number; case_count: number }

// super_admin: list companies and provision new ones (company + first admin).
export function AdminCompaniesPage() {
  const [companies, setCompanies] = useState<CompanyRow[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const { enterCompany } = useAuth()
  const navigate = useNavigate()

  function open(co: CompanyRow) {
    enterCompany({ id: co.id, name: co.name })
    navigate('/app/dashboard')
  }

  async function refresh() {
    setLoading(true)
    try {
      const res = await api.listCompanies()
      setCompanies(res.companies)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void refresh()
  }, [])

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div className="page-title">Companies</div>
          <p className="page-sub">Provision and manage client companies.</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'New Company'}
        </button>
      </div>

      {showForm && (
        <NewCompanyForm
          onCreated={() => {
            setShowForm(false)
            void refresh()
          }}
        />
      )}

      <div className="card" style={{ padding: 0, marginTop: 16 }}>
        {loading ? (
          <div style={{ padding: 20, color: 'var(--gray)' }}>Loading…</div>
        ) : companies.length === 0 ? (
          <div style={{ padding: 20, color: 'var(--gray)' }}>No companies yet.</div>
        ) : (
          <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Slug</th>
                <th>Users</th>
                <th>Cases</th>
                <th>Form link</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((co) => (
                <tr key={co.id}>
                  <td style={{ fontWeight: 600 }}>{co.name}</td>
                  <td style={{ color: 'var(--gray)' }}>{co.slug}</td>
                  <td>{co.user_count}</td>
                  <td>{co.case_count}</td>
                  <td>
                    <code style={{ fontSize: 12 }}>/f/{co.form_token}</code>
                  </td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn btn-primary" style={{ padding: '6px 14px' }} onClick={() => open(co)}>
                      Open
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        <AccountCard />
      </div>
    </div>
  )
}

function NewCompanyForm({ onCreated }: { onCreated: () => void }) {
  const [companyName, setCompanyName] = useState('')
  const [adminName, setAdminName] = useState('')
  const [adminEmail, setAdminEmail] = useState('')
  const [adminPassword, setAdminPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      await api.createCompany({ companyName, adminName, adminEmail, adminPassword })
      onCreated()
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'email_taken'
          ? 'That admin email is already in use.'
          : 'Could not create the company. Check the fields and try again.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form className="card" style={{ marginTop: 16, maxWidth: 480 }} onSubmit={onSubmit}>
      <div style={{ fontWeight: 700, marginBottom: 12 }}>New company</div>
      <div className="field">
        <label>Company name</label>
        <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} required />
      </div>
      <div className="field">
        <label>Admin name</label>
        <input value={adminName} onChange={(e) => setAdminName(e.target.value)} required />
      </div>
      <div className="field">
        <label>Admin email</label>
        <input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} required />
      </div>
      <div className="field">
        <label>Temporary password</label>
        <input
          type="text"
          value={adminPassword}
          onChange={(e) => setAdminPassword(e.target.value)}
          minLength={8}
          required
        />
      </div>
      {error && <p className="form-error">{error}</p>}
      <button className="btn btn-primary" type="submit" disabled={submitting}>
        {submitting ? 'Creating…' : 'Create company'}
      </button>
    </form>
  )
}
